import { memo, useMemo } from "react";
import { CORAL_LIGHT, PINK_BLOB, TEAL, usePrefersReducedMotion } from "./utils";

/**
 * Soft floating bokeh dots, recreated in pure CSS from the prototype's
 * BokehBackground (no raster assets — JUB rule 9). Dots are generated with a
 * seeded PRNG so the layout is stable across re-renders, and the float
 * animation is disabled under prefers-reduced-motion.
 */

// Small deterministic PRNG (mulberry32) so dots don't jump on re-render
const mulberry32 = (seed: number) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const DOT_COLORS = [CORAL_LIGHT, PINK_BLOB, TEAL];

export const BotezBokehBackground = memo(() => {
  const prefersReducedMotion = usePrefersReducedMotion();

  const dots = useMemo(() => {
    const random = mulberry32(20260615);
    return Array.from({ length: 36 }, (_, i) => ({
      id: i,
      left: random() * 100,
      top: random() * 100,
      size: random() * 8 + 4,
      opacity: random() * 0.25 + 0.08,
      delay: random() * 5,
      duration: random() * 3 + 4,
      color: DOT_COLORS[i % DOT_COLORS.length],
    }));
  }, []);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0" aria-hidden="true">
      {dots.map((dot) => (
        <div
          key={dot.id}
          className="absolute rounded-full"
          style={{
            left: `${dot.left}%`,
            top: `${dot.top}%`,
            width: `${dot.size}px`,
            height: `${dot.size}px`,
            opacity: dot.opacity,
            backgroundColor: dot.color,
            animation: prefersReducedMotion
              ? "none"
              : `botez-bokeh-float ${dot.duration}s ease-in-out ${dot.delay}s infinite alternate`,
            filter: "blur(2px)",
          }}
        />
      ))}
      <style>{`
        @keyframes botez-bokeh-float {
          0% { transform: translateY(0px); }
          100% { transform: translateY(-20px); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="botez-bokeh-float"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
});

BotezBokehBackground.displayName = "BotezBokehBackground";
